/**
 * Human-readable response messages for the LivDot platform.
 *
 * Used for API success payloads, notification bodies, and toast copy.
 * Grouped to mirror docs/integration — one sub-object per module.
 *
 * Rules of thumb:
 *  - Speak to the user, not about them ("your", not "the user's").
 *  - Never leak internal state (attempts remaining, provider names, ledger accounts).
 *  - Keep copy terse; the FE composes surrounding UI.
 *  - Do NOT put URLs, ids, or amounts in these strings — the FE renders those.
 */
export const ResponseMessages = {
  // ────────────────────────────────────────────────────────────────
  // 01 · Auth  (docs/integration/01-auth.md)
  // ────────────────────────────────────────────────────────────────
  AUTH: {
    REGISTER_SUCCESS: 'Account created. Check your email for the verification code.',
    REGISTER_EMAIL_EXISTS: 'An account with that email already exists. Try logging in instead.',
    OTP_SENT: 'We sent you a fresh code. It expires in a few minutes.',
    OTP_VERIFIED: 'Code verified.',
    OTP_INVALID: 'That code is invalid or has expired. Request a new one.',
    LOGIN_SUCCESS: 'Welcome back.',
    LOGIN_INVALID_CREDENTIALS: 'That email and password combination is incorrect.',
    LOGIN_ROLE_UNAVAILABLE:
      'You don’t have access to this app yet. Switch role or upgrade your account.',
    LOGIN_UNVERIFIED: 'Please verify your email before continuing. We resent the code.',
    LOGOUT_SUCCESS: 'You’ve been signed out.',
    REFRESH_SUCCESS: 'Session refreshed.',
    REFRESH_REVOKED: 'Your session has expired. Sign in again to continue.',
    PASSWORD_RESET_SENT: 'If an account exists for that email, a reset code is on its way.',
    PASSWORD_RESET_SUCCESS: 'Your password has been updated.',
    PASSWORD_CHANGE_SUCCESS: 'Password changed. Other sessions have been signed out.',
    ACCOUNT_DELETE_SUCCESS: 'Your account has been deleted.',
    ACCOUNT_DELETE_CONFIRM_MISMATCH:
      'The confirmation email doesn’t match. Type your email exactly as shown.',
    CONFIRM_PASSWORD_SUCCESS: 'Password confirmed. Continue with your change.',
    CONFIRM_PASSWORD_INVALID: 'That password is incorrect.',

    // OAuth
    OAUTH_PROVIDER_DISABLED: 'This sign-in method is temporarily unavailable. Try another option.',
    OAUTH_LINK_REQUIRED_EMAIL_MATCH:
      'An account with this email already exists. Verify to link them.',
    OAUTH_LINK_REQUIRED_EMAIL_MISSING:
      'We couldn’t get an email from your provider. Enter one to continue.',
    OAUTH_LINK_SUCCESS: 'Accounts linked.',
    OAUTH_INVALID_HANDOFF: 'This sign-in link has expired. Start again from the sign-in screen.',

    // MFA (TOTP)
    MFA_ENROL_STARTED: 'Scan the code with your authenticator app, then enter a code to enable.',
    MFA_ENABLED: 'Two-factor authentication is on.',
    MFA_DISABLED: 'Two-factor authentication is off.',
    MFA_CODE_INVALID: 'That code isn’t right. Try again with a fresh code.',
    MFA_NOT_ENROLLED: 'Set up two-factor authentication before continuing.',
    MFA_CHALLENGE_ISSUED: 'Enter your authenticator code to continue.',
    MFA_VERIFY_SUCCESS: 'Verified. You have 60 seconds to complete this action.',
    MFA_TOKEN_EXPIRED: 'This verification has expired. Start the security check again.',
    MFA_LOCKED: 'Too many failed attempts. Try again after the cooldown.',
    MFA_RECOVERY_CODES_REGENERATED:
      'New recovery codes generated. Save them now — we won’t show them again.',
  },

  // ────────────────────────────────────────────────────────────────
  // 02 · User & Profiles  (docs/integration/02-user-profiles.md)
  // ────────────────────────────────────────────────────────────────
  USER: {
    PROFILE_UPDATE_SUCCESS: 'Profile updated.',
    HANDLE_TAKEN: 'That handle is already taken. Try another.',
    CURRENCY_UPDATED: 'Display currency updated.',
    CURRENCY_UNSUPPORTED: 'That currency isn’t supported yet.',
    NOTIFICATION_PREFS_UPDATED: 'Notification preferences saved.',
    FEEDBACK_RECEIVED: 'Thanks for the feedback — we’ve got it.',
    PIN_SET: 'PIN set.',
    PIN_ROTATED: 'PIN updated.',
    PIN_CURRENT_INVALID: 'Your current PIN is incorrect.',

    HOST_PROFILE_CREATED: 'Host profile created. Set a PIN before your first payout.',
    HOST_PROFILE_UPDATED: 'Host profile updated.',
    HOST_PROFILE_EXISTS: 'You already have a host profile.',
    HOST_PROFILE_NOT_FOUND: 'You haven’t created a host profile yet.',

    VENDOR_PROFILE_CREATED: 'Vendor profile created. Submit for verification to appear in search.',
    VENDOR_PROFILE_UPDATED: 'Vendor profile updated.',
    VENDOR_PROFILE_EXISTS: 'You already have a vendor profile.',
    VENDOR_PROFILE_NOT_FOUND: 'You haven’t created a vendor profile yet.',
    VENDOR_VERIFY_SUBMITTED: 'Verification submitted. We’ll let you know once review is complete.',
  },

  // ────────────────────────────────────────────────────────────────
  // 03 · Host events + wizard + live console  (03-host-events.md)
  // ────────────────────────────────────────────────────────────────
  EVENT: {
    WIZARD_DRAFT_CREATED: 'Draft saved.',
    WIZARD_STEP_SAVED: 'Progress saved.',
    WIZARD_PUBLISHED: 'Event published — it’s now live to your audience.',
    WIZARD_PUBLISH_INCOMPLETE:
      'A few details are still missing. Finish the highlighted steps to publish.',
    WIZARD_NOT_DRAFT: 'This event can’t be edited from the wizard anymore.',

    UPDATED: 'Event details updated.',
    DELETED: 'Event deleted.',
    DELETE_NOT_ALLOWED: 'Only draft or empty scheduled events can be deleted.',
    PAUSED: 'Event paused.',
    UNPAUSED: 'Event resumed.',
    CANCELLED: 'Event cancelled. Refunds are processing and viewers have been notified.',
    CANCEL_NOT_ALLOWED: 'This event can no longer be cancelled.',

    // Stream test + go-live + end-live
    STREAM_TEST_READY: 'Stream credentials are ready. Copy the key now — you won’t see it again.',
    STREAM_TEST_RATE_LIMITED: 'You’ve run stream test too many times this hour. Try again later.',
    STREAM_TEST_WRONG_STATE: 'Stream test isn’t available in the current event state.',

    GO_LIVE_SOON: (time: string) => `Your event goes live in ${time}.`,
    GO_LIVE_SUCCESS: 'You’re live.',
    GO_LIVE_CHECKLIST_INCOMPLETE: 'Finish the pre-live checklist before going live.',
    GO_LIVE_BEFORE_WINDOW: 'Too early — the go-live window hasn’t opened yet.',
    GO_LIVE_AFTER_WINDOW: 'The go-live window has closed. Contact support.',
    GO_LIVE_ALREADY_LIVE: 'This event is already live.',
    GO_LIVE_NOT_OWNED: 'You’re not the host of this event.',

    END_LIVE_SUCCESS: 'Event ended. Replay will be available shortly.',
    END_LIVE_NOT_LIVE: 'This event isn’t live.',

    STREAM_CONTROL_UPDATED: 'Stream control updated.',

    REPLAY_PERIOD_EXTENDED: 'Replay availability updated.',
    REPLAY_PERIOD_NOT_ENDED: 'You can extend replay availability once the event ends.',

    NOT_OWNED: 'You don’t have access to this event.',
    NOT_FOUND: 'We couldn’t find that event.',

    // Post-event dashboard
    AUDIENCE_ROLLUP_PENDING: 'Audience insights will appear here once the rollup completes.',

    // Viewer feedback
    FEEDBACK_SAVED: 'Thanks for your feedback.',
    FEEDBACK_NOT_OPEN: 'Feedback opens once the event goes live.',
  },

  // ────────────────────────────────────────────────────────────────
  // 04 · Payouts, settlement, earnings  (04-payouts.md)
  // ────────────────────────────────────────────────────────────────
  PAYOUT: {
    WITHDRAW_ACCEPTED: 'Withdrawal requested. We’ll notify you once it reaches your bank.',
    WITHDRAW_PIN_INVALID: 'Incorrect PIN.',
    WITHDRAW_PIN_NOT_SET: 'Set a PIN in your profile before withdrawing.',
    WITHDRAW_SETTLEMENT_MISSING: 'Add a verified settlement account before withdrawing.',
    WITHDRAW_INSUFFICIENT_BALANCE: 'Your available balance is below the minimum withdrawal amount.',
    WITHDRAW_IDEMPOTENCY_MISSING: 'This request is missing a required key. Please retry.',
    WITHDRAW_IDEMPOTENCY_CONFLICT:
      'This request key has already been used. Start a new withdrawal.',
    WITHDRAW_PROVIDER_UNAVAILABLE: 'Payouts are temporarily unavailable. Try again shortly.',

    SETTLEMENT_UPDATED: 'Settlement account saved.',
    SETTLEMENT_ACCOUNT_UNVERIFIED:
      'We couldn’t verify that account. Double-check the bank and account number.',
    SETTLEMENT_NAME_MISMATCH: 'The bank account name doesn’t match the name on your profile.',
    SETTLEMENT_PROVIDER_UNAVAILABLE:
      'Bank verification is temporarily unavailable. Please try again.',

    ACCOUNT_RESOLVE_FAILED: 'That account number couldn’t be resolved. Check the digits and bank.',

    VIRTUAL_ACCOUNT_PROVISIONING:
      'Your deposit account is being set up. This usually takes under a minute.',
    VIRTUAL_ACCOUNT_READY: 'Your deposit account is ready.',
  },

  // ────────────────────────────────────────────────────────────────
  // 05 · Marketplace  (05-marketplace.md)
  // ────────────────────────────────────────────────────────────────
  MARKETPLACE: {
    // Job listings
    LISTING_DRAFT_SAVED: 'Draft saved.',
    LISTING_PUBLISHED: 'Job listing published.',
    LISTING_PUBLISH_INCOMPLETE: 'Fill in the required fields before publishing.',
    LISTING_UPDATED: 'Job listing updated.',
    LISTING_CANCELLED: 'Job listing cancelled.',
    LISTING_DELETED: 'Draft deleted.',
    LISTING_NOT_DRAFT: 'Only draft listings can be deleted.',
    LISTING_NOT_OPEN: 'This job listing is no longer accepting applications.',
    LISTING_ALREADY_EXISTS: 'You already have a listing for this event.',

    // Indications of interest
    IOI_SUBMITTED: 'Your interest has been sent to the host.',
    IOI_ALREADY_SUBMITTED: 'You’ve already applied to this listing. Wait for the host to respond.',
    IOI_WITHDRAWN: 'Application withdrawn.',
    IOI_WITHDRAW_NOT_ALLOWED: 'This application can no longer be withdrawn.',

    // Bookings
    BOOKING_CREATED: 'Booking created. The vendor has been notified.',
    BOOKING_STATE_UPDATED: 'Booking updated.',
    BOOKING_TRANSITION_FORBIDDEN: 'You’re not allowed to make this change on this booking.',
    BOOKING_TRANSITION_INVALID: 'That change isn’t allowed from the current state.',
    BOOKING_ESCROW_UNFUNDED: 'Escrow must be funded before this booking can be confirmed.',
    BOOKING_REASON_REQUIRED: 'Add a short reason to continue.',
    BOOKING_ACCEPTED: 'Booking accepted.',
    BOOKING_REJECTED: 'Booking rejected.',
    BOOKING_CANCELLED: 'Booking cancelled.',
    BOOKING_COMPLETION_ACK: 'Your completion is recorded. Waiting on the other party to confirm.',
    BOOKING_COMPLETED: 'Service marked complete. Funds will release after the dispute window.',
    BOOKING_NOT_PARTICIPANT: 'You’re not a participant on this booking.',

    // Reviews
    REVIEW_SUBMITTED: 'Thanks for your review.',
    REVIEW_ALREADY_EXISTS: 'You’ve already reviewed this booking.',
    REVIEW_NOT_REVIEWABLE:
      'Reviews are available once service is completed (or the booking is closed).',
    BOOKING_REVIEW_REQUIRED_BEFORE_RELEASE:
      'Submit a review for the vendor before closing this booking and releasing funds.',

    // Hiring thread
    THREAD_MESSAGE_SENT: 'Message sent.',
    THREAD_MESSAGE_TOO_LONG: 'Your message is too long. Trim it and try again.',
    THREAD_CLOSED: 'This thread is closed. You can no longer send messages here.',
    THREAD_READ_RECEIPTS_UPDATED: 'Messages marked as read.',

    // Direct Event Hire — crew role assignments
    CREW_ASSIGNMENT_SAVED: 'Crew role saved.',
    CREW_ASSIGNMENT_UPDATED: 'Crew role updated.',
    CREW_ASSIGNMENT_REMOVED: 'Crew role removed.',
    CREW_ASSIGNMENT_NOT_FOUND: 'That crew assignment was not found.',
    CREW_ASSIGNMENT_INVALID_STATE: 'That action isn’t allowed for this invite right now.',
    CREW_ASSIGNMENT_ALREADY_EXISTS: 'This vendor already has a role on this event.',
    CREW_ASSIGNMENT_BOOKING_EXISTS:
      'A booking already exists for this vendor on the event. Remove it before inviting again.',
    CREW_ASSIGNMENT_ACCEPTED: 'Invite accepted. Your booking is ready.',
    CREW_ASSIGNMENT_REJECTED: 'Invite declined.',
    CREW_ASSIGNMENT_INVITED: 'You’ve been invited to join a production crew.',
    CREW_ASSIGNMENT_REPLACED: 'Crew member replaced and invite sent.',
    CREW_ASSIGNMENT_NOT_REPLACABLE: 'Only cancelled or declined crew roles can be replaced.',
    CREW_ASSIGNMENT_ALREADY_REPLACED: 'This crew role has already been replaced.',

    // Booking disputes (inbox + email copy)
    DISPUTE_OPENED_TITLE: 'Booking dispute opened',
    DISPUTE_OPENED_BODY_RAISER:
      'Your dispute on this booking was submitted. Escrow is on hold until it is resolved.',
    DISPUTE_OPENED_BODY_COUNTERPARTY:
      'A dispute was opened on this booking. Escrow is on hold until it is resolved.',
    DISPUTE_RESOLVED_TITLE: 'Booking dispute resolved',
    DISPUTE_RESOLVED_BODY: 'The dispute on this booking was resolved.',
    DISPUTE_RESOLVED_OUTCOME_REFUNDED: 'refunded',
    DISPUTE_RESOLVED_OUTCOME_PARTIAL_REFUND: 'partially refunded',
    DISPUTE_RESOLVED_OUTCOME_DENIED: 'denied',
  },

  // ────────────────────────────────────────────────────────────────
  // 06 · Payments  (06-payments.md)
  // ────────────────────────────────────────────────────────────────
  PAYMENT: {
    QUOTE_ISSUED: 'Price quote ready.',
    PURCHASE_INITIATED: 'Redirecting you to checkout.',
    PURCHASE_PENDING: 'We’re confirming your payment. This usually takes a few seconds.',
    PURCHASE_SUCCESS: 'Payment received. You’re in.',
    PURCHASE_FAILED: 'Payment failed. Try again or use a different method.',
    PURCHASE_CANCELLED: 'You cancelled the payment.',
    PURCHASE_EXPIRED: 'Your checkout session expired. Start the purchase again.',
    PURCHASE_NOT_FOUND: 'We couldn’t find that purchase.',
    PROVIDER_UNAVAILABLE: 'Payments are temporarily unavailable. Please try again shortly.',
    IDEMPOTENCY_KEY_REQUIRED: 'Your app didn’t send a payment key. Refresh and try again.',

    REGISTRATION_FREE_SUCCESS: 'You’re registered.',
    REGISTRATION_PAID_PENDING: 'Complete payment to confirm your registration.',
    REGISTRATION_CANCELLED: 'Your registration has been cancelled.',
    REGISTRATION_ALREADY_ACTIVE: 'You’re already registered for this event.',
    REGISTRATION_SOLD_OUT: 'Sorry — this event is sold out.',
    REGISTRATION_PRIVATE_NOT_ALLOWED: 'You’re not on the guest list for this event.',
    REGISTRATION_REPLAY_CLOSED: 'The replay window has closed for this event.',

    ACCESS_CHECK_QUALIFIED: 'You’re on the guest list — go ahead and register.',
    ACCESS_CHECK_NOT_QUALIFIED:
      'This email isn’t on the guest list. Try another, or contact the host.',

    RECEIPT_READY: 'Receipt ready.',
    RECEIPT_NOT_AVAILABLE: 'The receipt will be available once payment is confirmed.',
    CALENDAR_READY: 'Calendar file ready.',

    REPLAY_ACCESS_INCLUDED: 'Replay unlocked — enjoy.',
    REPLAY_PURCHASE_PENDING: 'Complete payment to unlock replay.',
    REPLAY_DISABLED: 'The host disabled replay for this event.',
    REPLAY_REGISTRATION_REQUIRED: 'You need an active ticket before you can watch this replay.',

    REFUND_PROCESSING:
      'Refund is being processed. It usually completes within a few business days.',
    REFUND_COMPLETE: 'Refund complete.',
  },

  // ────────────────────────────────────────────────────────────────
  // 07 · Streaming  (07-streaming.md)
  // ────────────────────────────────────────────────────────────────
  STREAMING: {
    PLAYBACK_LIVE_READY: 'You’re watching live.',
    PLAYBACK_REPLAY_READY: 'Replay ready.',
    PLAYBACK_NOT_ENTITLED: 'You need a ticket or replay pass to watch this event.',
    PLAYBACK_REPLAY_ONLY_NOT_LIVE:
      'This pass unlocks the replay. We’ll let you know once it’s ready.',
    PLAYBACK_UNAVAILABLE: 'This event isn’t playable yet. Check back closer to start time.',
    PLAYBACK_REPLAY_EXPIRED: 'The replay window for this event has closed.',
    PLAYBACK_SESSION_ELSEWHERE: 'You’re watching on another device. Resume here to switch.',
    PLAYBACK_KILLSWITCH: 'Playback is temporarily disabled by the host or support.',

    CREW_HEARTBEAT_OK: 'Presence updated.',
    CREW_NOT_ASSIGNED: 'You’re not confirmed crew on this event.',

    CREW_NOT_LIVE: 'This event isn’t live yet.',
    CREW_ALREADY_LIVE: 'This event is already live.',
    CREW_NOT_OWNED: 'You’re not the host of this event.',
    CREW_STREAM_NOT_READY: 'The stream isn’t ready yet. Check back in a few minutes.',
    CREW_STREAM_TEST_NOT_READY: 'The stream test isn’t ready yet. Try again in a few minutes.',
    INGEST_DISCONNECTED: 'Stream ingest disconnected',
    INGEST_RECONNECTING: 'Stream ingest reconnected',
  },

  // ────────────────────────────────────────────────────────────────
  // 08 · Chat  (08-chat.md)
  // ────────────────────────────────────────────────────────────────
  CHAT: {
    MESSAGE_SENT: 'Message sent.',
    MESSAGE_TOO_LONG: 'Messages can’t be longer than 500 characters.',
    MESSAGE_EMPTY: 'Type a message before sending.',
    MESSAGE_PROFANITY: 'Message blocked by moderation.',
    ROOM_MUTED: 'Chat is muted right now.',
    ROOM_NOT_LIVE: 'Chat is closed for this event.',
    ROOM_MUTED_BY_HOST: 'You’ve muted the room.',
    ROOM_UNMUTED_BY_HOST: 'Room unmuted.',
    MESSAGE_DELETED: 'Message removed.',
    MESSAGE_ALREADY_DELETED: 'That message has already been removed.',
    RATE_LIMITED: 'You’re sending too fast. Slow down a moment.',
    NOT_ENTITLED: 'You need an active registration to chat.',
  },

  // ────────────────────────────────────────────────────────────────
  // 09 · Notifications & push devices  (09-notifications.md)
  // ────────────────────────────────────────────────────────────────
  NOTIFICATIONS: {
    DEVICE_REGISTERED: 'Push notifications enabled on this device.',
    DEVICE_REVOKED: 'Push notifications disabled on this device.',
    DEVICE_TOKEN_INVALID: 'That push token isn’t valid. Try again after re-opening the app.',
    NOTIFICATION_MARKED_READ: 'Marked as read.',
    ALL_MARKED_READ: 'All caught up.',
    ALL_CLEARED: 'Notifications cleared.',
    NOTIFICATION_NOT_FOUND: 'We couldn’t find that notification.',
  },

  // ────────────────────────────────────────────────────────────────
  // 10 · Public share links  (10-share.md)
  // ────────────────────────────────────────────────────────────────
  SHARE: {
    LINK_RESOLVED: 'Loading event.',
    LINK_NOT_FOUND: 'This share link isn’t available anymore.',
  },

  // ────────────────────────────────────────────────────────────────
  // 11 · Viewer events + sessions  (11-viewer-events.md)
  // ────────────────────────────────────────────────────────────────
  VIEWER: {
    SESSION_STARTED: 'Session started.',
    SESSION_ENDED: 'Session ended.',
    SESSION_ALREADY_ENDED: 'This viewing session has already ended.',
    WATCH_PROGRESS_SAVED: 'Progress saved.',
    WATCH_PROGRESS_NOT_REPLAYABLE: 'Watch progress is only saved during replays.',
  },

  // ────────────────────────────────────────────────────────────────
  // General / cross-cutting
  // ────────────────────────────────────────────────────────────────
  GENERAL: {
    SUCCESS: 'Done.',
    ERROR: 'Something went wrong. Please try again — if it keeps happening, contact support.',
    VALIDATION_ERROR: 'Check the highlighted fields and try again.',

    UNAUTHORIZED: 'Please sign in to continue.',
    FORBIDDEN: 'You don’t have permission to do that.',
    NOT_FOUND: 'We couldn’t find what you were looking for.',
    CONFLICT: 'That action isn’t allowed from the current state.',
    PRECONDITION_FAILED: 'A required setup step is missing. Complete it, then try again.',
    LOCKED: 'Too many attempts. Try again in a few minutes.',
    RATE_LIMITED: 'You’re doing that too often. Wait a moment and try again.',
    SERVICE_UNAVAILABLE: 'This feature is temporarily unavailable. Try again shortly.',
    UNSUPPORTED_CURRENCY: 'That currency isn’t supported.',

    IDEMPOTENCY_KEY_REQUIRED: 'Missing request key. Refresh and try again.',
    IDEMPOTENCY_KEY_CONFLICT: 'This request key was already used. Retry with a fresh action.',
  },
} as const;

export type ResponseMessages = typeof ResponseMessages;
